<template>
  <div class="intervention-plan-continer">
    <page-header
      :actions="pageHeaderActionsConfig"
      left-side-class="col-md-7"
      :page="pageHeaderConfig"
      right-side-class="col-sm-12 col-md-5 align-items-end d-flex pb-2 justify-content-end"
    >
      <dropdown
        v-if="showDropdownActionsButtons"
        class="actions-dropdown"
        :items="dropdownActionItems"
        title="Acciones"
      />
    </page-header>
    <absence-intervention-plan
      v-if="showAbsenceDocumentation"
      :absence-reasons="collections.absenceReasons"
      class="mb-2"
      :linked-date="admissionInfo.linkedDate"
      @on-cancel="() => (showAbsenceDocumentation = false)"
      @on-save="onSaveAbsenceInterventionHandler($event)"
    />
    <invoice-plan
      v-if="showInvoice && !isLoadingPlanInfo"
      ref="invoicePlan"
      :continue-editing="getInvoiceContinueEditing"
      :info="invoice"
      :intervention-flags="interventionFlags"
      :is-intervention-plan-electronic-signed="
        isInterventionPlanElectronicSigned
      "
      :is-plan-amendment="showAmendedPlanHistoryButton"
      :is-sign-hub-active="isSignHubActive"
      :max-date="maxDate"
      :only-show-plan="onlyShowPlan"
      :read-only="invoiceReadOnly"
      :specialists="collections.writerSpecialists"
      wrapper-class="mb-3"
      @alert="validationToast"
      @alert-billing-change="alertBillingChangeHandler"
      @cancel="cancelHandler"
      @edit-mode="editMode"
      @is-plan-billing-change="isPlanBillingChange"
      @save-info="invoiceSaveHandler"
    />
    <board :details="invoiceDetailsBoard" />
    <execution-level-legend
      v-if="achievementSummary"
      :legend-data="executionLevelArchieved"
      title="Leyenda de Claves para objetivos de resumen de logros"
    />
    <b-tabs
      v-model="tabModel"
      class="mt-3"
      nav-class="custom-nav-container"
      :no-nav-style="true"
    >
      <b-tab
        :active="true"
        :title="
          achievementSummary ? 'Informe de Logros' : 'Plan de Intervención'
        "
      >
        <div class="mx-2">
          <custom-collapse
            id="ip-collapse"
            class="intervention-plan-collapse mt-3 px-2"
          >
            <custom-collapse-item
              id="collapse-item-strength"
              body-background-color="#F7F7F7"
              :checked="checkedStrengths"
              :expanded="true"
              parent-id="ip-collapse"
              title="Información de Fortalezas"
            >
              <strengthSelection
                v-if="disciplineTypeId > 0"
                ref="strengthSelection"
                :discipline-type-id="disciplineTypeId"
                :read-only="readOnly"
                :strengths="strengths"
                @save-strengths="savestrengthsHandler"
              />
            </custom-collapse-item>
            <custom-collapse-item
              id="collapse-item-need"
              body-background-color="#F7F7F7"
              :checked="checkedNeed"
              :expanded="false"
              parent-id="ip-collapse"
              title="Información de Necesidades"
            >
              <NeedSelection
                v-if="disciplineTypeId > 0"
                ref="NeedSelection"
                :discipline-type-id="disciplineTypeId"
                :needs="needs"
                :read-only="readOnly"
                @save-needs="saveNeedsHandler"
              />
            </custom-collapse-item>
            <custom-collapse-item
              id="collapse-item-goals"
              body-background-color="#F7F7F7"
              :button-text="goals.length != 0 && !readOnly ? 'Añadir Meta' : ''"
              :checked="checkedGoals"
              class="collapse-item"
              :expanded="false"
              parent-id="ip-collapse"
              title="Información de Metas y Objetivos"
              @collapseItemButtonClick="addGoal()"
            >
              <div class="p-2 mb-2">
                <div
                  v-if="goals.length == 0 && !showAddGoal"
                  class="bg-white p-3 row justify-content-center"
                >
                  <button
                    class="btn col-9 col-md-6 bg-light-white py-0 font-weight-normal"
                    @click="addGoal()"
                  >
                    <em class="fas fa-file-plus pr-3 text-green" />
                    <span>Presione aquí para añadir meta</span>
                  </button>
                </div>
                <validation-observer v-else ref="interventionPlanGoal">
                  <div
                    v-for="(goal, index) in goals"
                    :key="`goals-${index}-${goal.id}`"
                  >
                    <goal-form
                      v-if="goal.editGoal"
                      :key="`goal-form-${index}`"
                      :area-options="areaOptions"
                      class="mb-3"
                      :data="goal"
                      :goal-options="goalOptions"
                      :is-pei-signed="isPeiSigned"
                      :show-area-selection="showAreaSelection"
                      :student-has-educational-service="
                        studentHasEducationalService
                      "
                      @cancel-update="cancelUpdateHandler(index)"
                      @edit-goal="editGoalHandler($event, index)"
                    />
                    <intervention-plan-goals
                      v-else
                      :key="`goal-${index}`"
                      :achievement-summary="achievementSummary"
                      :data="goal"
                      :legend-data="executionFormData"
                      :position="index + 1"
                      :read-only="readOnly"
                      :show-area-selection="showAreaSelection"
                      :submitted-summaries="submittedSummaries"
                      @delete-goal="deleteGoalHandler(index)"
                      @edit-goal="showEditGoalHandler(index)"
                      @on-change-data="changeDataHandler"
                    />
                  </div>
                  <goal-form
                    v-if="showAddGoal"
                    :area-options="areaOptions"
                    :goal-options="goalOptions"
                    :is-pei-signed="isPeiSigned"
                    :show-area-selection="showAreaSelection"
                    :student-has-educational-service="
                      studentHasEducationalService
                    "
                    @cancel-add="cancelGoalHandler"
                    @save-goal="saveGoalHandler"
                  />
                </validation-observer>
              </div>
            </custom-collapse-item>
            <custom-collapse-item
              id="collapse-item-criterion"
              body-background-color="#F7F7F7"
              :checked="checkedCriteria"
              :expanded="false"
              parent-id="ip-collapse"
              title="Criterios y Procedimientos"
            >
              <validation-observer ref="interventionPlanCriterias">
                <intervention-plan-criterias
                  :data="criterias"
                  :read-only="readOnly"
                  @cancel-criteria="cancelCriteriahandler"
                  @delete-criteria="deleteCriteriaHandler"
                  @save-criteria="saveCriteriaHandler"
                />
              </validation-observer>
            </custom-collapse-item>
            <custom-collapse-item
              id="collapse-item-specialist"
              body-background-color="#F7F7F7"
              :checked="checkedSpecialists"
              :expanded="false"
              parent-id="ip-collapse"
              title="Información de Especialista"
            >
              <div>
                <div class="row">
                  <div class="col-6">
                    <validation-provider rules="required">
                      <custom-drop-down
                        id="serviceSpecialist"
                        v-model="specialistInfo.serviceSpecialist"
                        :disabled="readOnly"
                        :error="errors[0]"
                        fieldkey="id"
                        fieldname="name"
                        label="Especialista de Servicio: "
                        name="serviceSpecilist"
                        :options="collections.serviceSpecialists"
                        @input="getSpecialistInfo()"
                      />
                    </validation-provider>
                    <hr />
                  </div>
                </div>
                <div class="row">
                  <div class="col-6">
                    <validation-provider rules="required">
                      <custom-drop-down
                        id="writerSpecialist"
                        v-model="specialistInfo.writerSpecialist"
                        :disabled="readOnly"
                        :error="errors[0]"
                        fieldkey="id"
                        fieldname="name"
                        label="Persona que redactó el plan: "
                        name="writerSpecialist"
                        :options="collections.writerSpecialists"
                      />
                    </validation-provider>
                    <hr />
                  </div>
                </div>
                <div class="row col-md-12">
                  <div class="row bg-white w-100 ml-1" style="min-height: 8vh">
                    <div class="col-md-4 mt-4">
                      <div class="color-secondary-6 font-14">
                        Especialista de Servicio:
                      </div>
                      <span class="color-black font-16 font-weight-bold">
                        {{ specialistInfo.selectedServiceSpecialist.fullName }}
                      </span>
                    </div>
                    <div class="col-md-4 mt-4">
                      <div class="color-secondary-6 font-14">
                        Numero de Licencia:
                      </div>
                      <span class="color-black font-16">
                        {{ specialistInfo.selectedServiceSpecialist.licenceNo }}
                      </span>
                    </div>
                    <div class="col-md-4 mt-4">
                      <div class="color-secondary-6 font-14">
                        Fecha de Expiración:
                      </div>
                      <span class="color-black font-16">
                        {{
                          specialistInfo.selectedServiceSpecialist
                            .expirationDate
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </custom-collapse-item>
            <custom-collapse-item
              v-if="canViewDocumentSection"
              id="collapse-item-document"
              body-background-color="#F7F7F7"
              :checked="documentSaved"
              :expanded="false"
              parent-id="ip-collapse"
              title="Documento"
            >
              <validation-observer ref="interventionPlanDocument">
                <intervention-plan-document
                  :data="document"
                  :saved="documentSaved"
                  @cancel-document="cancelDocumenthandler"
                  @edit-document="editDocumentHandler"
                  @save-document="saveDocumentHandler"
                />
              </validation-observer>
            </custom-collapse-item>
          </custom-collapse>
          <div v-if="validate">
            <custom-error
              v-if="errors.strength.invalid"
              :message="errors.strength.message"
            />
            <custom-error
              v-if="errors.need.invalid"
              :message="errors.need.message"
            />
            <custom-error
              v-if="errors.goals.invalid"
              :message="errors.goals.message"
            />
            <custom-error
              v-if="errors.criteria.invalid"
              :message="errors.criteria.message"
            />
            <custom-error
              v-if="errors.specialist.invalid"
              :message="errors.specialist.message"
            />
            <custom-error
              v-if="!isSignHubActive && errors.document.invalid"
              :message="errors.document.message"
            />
            <custom-error
              v-if="
                summaryErrors().executionLevel.invalid && achievementSummary
              "
              :message="summaryErrors().executionLevel.message"
            />
          </div>
          <template v-if="achievementSummary">
            <achievements-summary
              :data="archievements"
              :is-sign-hub-active="isSignHubActive"
              :periods="selectedPeriodsToSubmit"
              :read-only="summaryReadOnly"
              :signature-hub-info="signatureHubInfo"
              :specialists="collections.writerSpecialists"
              @alert="validationToast"
              @delete-achieve="deleteGoalsSummaryHandler"
              @delete-archivement="deleteArchivement"
              @save="saveGoalSummaryHandler"
            />

            <custom-error
              v-if="
                summaryErrors().achievement.invalid && validate && !partialSave
              "
              :message="summaryErrors().achievement.message"
            />
          </template>
        </div>
      </b-tab>
      <b-tab
        v-if="canViewSignPlanTab"
        :title="achievementSummary ? 'Firma' : 'Firma del Plan'"
      >
        <div class="my-4">
          <sign-plan
            ref="signPlanRef"
            :disabled="signPlanRefDisabled"
            :is-electronic-signature="isElectronicSignature"
            :question-label="questionLabel"
            @change="handleSelectMechanism"
          />
          <div class="mt-4">
            <div v-if="hasSelectMechanismManuscrito">
              <section-title content="Documento" has-line />
              <div class="bg-secondary-13 rounded p-3">
                <validation-observer ref="interventionPlanSignatureDocument">
                  <intervention-plan-signature-document
                    ref="interventionPlanSignatureDocumentRef"
                    :data="document"
                    :read-only="signatureDocumentReadOnly"
                    :saved="documentSaved"
                    :title="signatureDocumentTitle"
                    @cancel-document="handleCancelSignatureDocument"
                    @edit-document="editDocumentHandler"
                    @save-document="
                      (docFile) => saveDocumentHandler(docFile, false)
                    "
                  />
                </validation-observer>
              </div>
              <div class="text-right mt-3">
                <action-button
                  v-if="showSaveDocumentButton && hasDocumentFile"
                  i="pr-1 fas fa-save"
                  text="Guardar Documento"
                  variant="outline-success mx-2"
                  @click="
                    achievementSummary && hasSelectMechanismManuscrito
                      ? saveAchievementSummaryDocumentHandler()
                      : saveDocumentPlanHandler()
                  "
                />
              </div>
            </div>
            <signature-hub-info
              v-if="hasSelectMechanismElectronica"
              :signature-info="signatureHubInfo"
              @configure-sign="handleConfigureSignatures"
            />
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import alertMixin from 'utils/mixins/alert.mixin';
import Board from 'Board';
import {
  getStudentReferralAdmision,
  getSpecialistsForAssignment,
  getAbsenceReason,
  getInterventionPlanAvailableServiceAreas as _getInterventionPlanAvailableServiceAreas,
  getInterventionPlanAvailableGoals as _getInterventionPlanAvailableGoals,
} from '@/modules/shared/studentreferral.api';
import {
  submitInterventionPlan,
  getCodeExecutionLevelArchieved,
  getCurrentPeriod,
  submitInterventionPlanSummary,
  getInterventionPlan,
  deleteArchivement,
  savePlanDiscussedWithParentsFlag,
  saveDiscussedDocument,
  getSpecialistInfo as _getSpecialistInfo,
  saveAbsence,
  saveInterventionPlanMechanism as _saveInterventionPlanMechanism,
  saveAchievementSummaryDocument as _saveAchievementSummaryDocument,
  getAchievementSummaryDocument as _getAchievementSummaryDocument,
} from '@/modules/referralManagement/api';
import { validateIsActiveDayOfWeek as _validateIsActiveDayOfWeek } from '@/modules/shared/confRelatedServiceAcademicWeek.api';
import {
  getFutureAcademicYear as _getFutureAcademicYear,
  getAcademicYear as _getAcademicYear,
} from '@/modules/shared/academicyear.api';
import { getSysparameterByNames as _getSysparameterByNames } from '@/modules/shared/sysparameter.api';
import { CustomCollapse, CustomCollapseItem } from 'custom-collapse';
import InterventionPlanGoals from '@/views/provider/referral-management/search/components/InterventionPlanGoals.vue';
import InterventionPlanCriterias from '@/views/provider/referral-management/search/components/InterventionPlanCriterias.vue';
import InterventionPlanDocument from '@/views/provider/referral-management/search/components/InterventionPlanDocument.vue';
import InterventionPlanSignatureDocument from '@/views/provider/referral-management/search/components/InterventionPlanSignatureDocument.vue';
import strengthSelection from '@/views/provider/referral-management/search/components/StrenghtSelection.vue';
import NeedSelection from '@/views/provider/referral-management/search/components/NeedSelection.vue';
import GoalForm from '@/views/provider/referral-management/search/components/GoalForm.vue';
import AchievementsSummary from '@/views/provider/referral-management/search/components/AchievementsSummary.vue';
import InvoicePlan from '@/views/provider/referral-management/search/components/InvoicePlan.vue';
import ExecutionLevelLegend from '@/views/provider/referral-management/search/components/ExecutionLevelLegend.vue';
import CustomError from 'custom-error';
import interventionPlanMeasure from '@/utils/shared/constants/interventionPlanMeasure';
import interventionPlanPeriods from '@/utils/shared/constants/interventionPlanPeriods';
import AdmissionStatus from '@/utils/shared/constants/admissionStatus';
import ConfRelatedAcademicWeekFlagTypes from '@/utils/shared/constants/confRelatedAcademicWeekFlagTypes';
import { OPTION_BILLING_PLAN_DISCUSSION_YES } from '@/views/provider/referral-management/search/components/InvoicePlan.vue';
import sysparameterByNames from '@/utils/constants/sysParameterNames';
import moment from 'moment';
import _ from 'lodash';
import { serialize as objectToFormData } from 'object-to-formdata';
import _generalApi from '@/modules/app/api';
import CustomDropDown from 'custom-drop-down';
import { specialistsList as getSpecialistList } from '@/modules/provider/api';
import {
  getSignatureHubInfo as _getSignatureHubInfo,
  removeSignatureHub as _removeSignatureHub,
  getSignedDocument as _getSignedDocument,
} from '@/modules/signatureHub/api';
import AbsenceInterventionPlan from './components/AbsenceInterventionPlan.vue';
import { PageHeader } from '@/shared/components/PageHeader';
import Dropdown from 'dropdown';
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import { SignPlan } from './components/sign-plan';
import signatureHubTypes from '@/utils/constants/signatureHubTypes.js';
import DigitalSignatureDocumentStatus, {
  DigitalSignatureDocumentStatusColor,
} from '@/utils/constants/digitalSignatureDocumentStatus';
import { SignatureHubInfo } from '@quisit/signature-hub';
import documentTypes from '@/utils/shared/constants/documentTypes';
import { getLastPeibyStudentsie as _getLastPeibyStudentsie } from '@/modules/shared/pei.api';
import PeiStatuses from '@/utils/constants/peiStatuses';

const AMENDED_PLAN_HISTORY_ROUTE_NAME = 'amendedPlanHistory';

export default {
  name: 'InterventionPlan',
  components: {
    PageHeader,
    Board,
    CustomCollapse,
    CustomCollapseItem,
    InterventionPlanGoals,
    GoalForm,
    AchievementsSummary,
    ExecutionLevelLegend,
    InterventionPlanCriterias,
    InterventionPlanDocument,
    InterventionPlanSignatureDocument,
    CustomError,
    InvoicePlan,
    CustomDropDown,
    strengthSelection,
    NeedSelection,
    AbsenceInterventionPlan,
    Dropdown,
    SectionTitle,
    SignPlan,
    SignatureHubInfo,
    ActionButton,
  },
  mixins: [alertMixin],
  props: {
    achievementSummary: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    submittedSummaries: null,
    partialSave: false,
    futureAcademicYear: null,
    academicYear: null,
    sysParameters: [],
    admissionInfo: {},
    lastPei: {},
    specialistInfo: {
      serviceSpecialist: null,
      admissionSpecialist: null,
      writerSpecialist: null,
      selectedServiceSpecialist: {
        fullName: '',
        licenceNo: '',
        expirationDate: '',
      },
    },
    goals: [],
    strengths: [],
    strengthsSelected: [],
    needs: [],
    needsSelected: [],
    criterias: '',
    document: { file: null },
    documentOriginal: { file: null },
    showAddGoal: false,
    editGoal: false,
    dataBackup: {
      invoice: {},
    },
    invoice: {
      date: null,
      isPlanBilling: null,
      endTime: null,
      startTime: null,
    },
    date: null,
    interventionFlags: {
      isSubmitted: false,
      isAmending: false,
      hasHistory: false,
      hasParent: false,
      preSubmit: false,
    },
    validate: false,
    // summary
    archievements: [],
    avaliablePeriods: [],
    executionLevelArchieved: [],
    summaryReadOnly: false,
    from: 'referral-search',
    fromData: {
      reload: true,
    },
    routeName: '',
    collections: {
      serviceSpecialists: [],
      writerSpecialists: [],
      admissionSpecialists: [],
      absenceReasons: [],
    },
    documentSaved: false,
    isPlanBilling: false,
    invoiceContinueEditing: false,
    showAbsenceDocumentation: false,
    selectedMechanismSignPlan: null,
    tabModel: null,
    signatureHubInfo: null,
    hasDocumentFile: false,
    hasSignDocument: false,
    areaOptions: [],
    goalOptions: [],
    activeInvoicePlan: false,
    activeInvoicePlanByAction: false,
    hasPlanDiscussion: false,
    editingDocument: false,
    isLoadingPlanInfo: false,
  }),
  computed: {
    fromThisAcademicYearForward() {
      return this.academicYear?.year >= 2027;
    },
    isPeiSigned() {
      if (this.lastPei) {
        return (
          this.lastPei.peiStatusCode == PeiStatuses.Firmado ||
          this.lastPei.peiStatusCode == PeiStatuses.CompletedWithPendingFunds ||
          this.lastPei.peiStatusCode == PeiStatuses.PartialSignedPendingFunds
        );
      }
      return false;
    },
    isAmmendedPlan() {
      return this.interventionFlags.hasParent; // || this.interventionFlags.isAmending
    },
    navParams() {
      return {
        from: this.$route.params.from || this.from,
        fromData: this.$route.params.fromData || this.fromData,
      };
    },
    maxDate() {
      return this.admissionInfo.admissionStatusId ==
        AdmissionStatus.NoVigenteEgreso
        ? moment(this.admissionInfo.effectiveDateTo)
        : new Date();
    },
    getInvoiceContinueEditing() {
      return this.invoiceContinueEditing;
    },
    selectedPeriodsToSubmit() {
      //Todos los objetivos deben estar evaluados en un periodo específico para poder mostrar el periodo en la sección de resumen de logros.
      let periods = [];
      this.avaliablePeriods.forEach((item) => {
        var add = true;
        this.goals.forEach((goal) => {
          goal.objectives.forEach((objetive) => {
            if (
              objetive.executionLevelArchieved.filter(
                (x) => x.period.periodId == item.periodId
              ).length > 0
            ) {
              add;
            } else {
              add = false;
            }
          });
        });
        let isPeriodSubmitted =
          this.archievements.find((x) => x.period.periodId == item.periodId) ==
          undefined;
        if (add && isPeriodSubmitted) periods.push(item);
      });
      return periods;
    },
    selectedPeriods() {
      let periods = [];
      this.goals.forEach((goal) => {
        goal.objectives.forEach((objective) => {
          periods = periods.concat(
            (objective.executionLevelArchieved || [])
              .filter(
                (o) =>
                  periods.every((p) => p.periodId != o.period.periodId) &&
                  this.archievements.every(
                    (p) => p.period.periodId != o.period.periodId
                  )
              )
              .map((p) => ({ ...p.period }))
          );
        });
      });
      return periods;
    },
    checkedGoals() {
      return (
        this.goals.length > 0 &&
        this.goals.every(
          (g) =>
            g.objectives.length > 0 &&
            (!this.studentHasEducationalService ||
              (g.areaSelection.length > 0 && g.goalSelection.length > 0))
        )
      );
    },
    checkedCriteria() {
      return Boolean(this.criterias.length != 0);
    },
    checkedStrengths() {
      return Boolean(this.strengthsSelected.length != 0);
    },
    checkedNeed() {
      return Boolean(this.needsSelected.length != 0);
    },
    checkedSpecialists() {
      return !!(
        this.specialistInfo.serviceSpecialist &&
        this.specialistInfo.writerSpecialist
      );
    },
    getIsPlanBilling() {
      return this.isPlanBilling ?? false;
    },
    summaryErrors() {
      return (objectiveArchievements = 0) => {
        return {
          executionLevel: {
            invalid: !objectiveArchievements,
            message:
              'Favor agregar el nivel de ejecución logrado de al menos uno de los objetivos.',
          },
          achievement: {
            invalid: !this.archievements.length,
            message:
              'Favor añadir el resumen de logros del periodo correspondiente.',
          },
          specialist: this.specialistErrors,
        };
      };
    },
    errors() {
      return {
        strength: {
          invalid:
            this.strengthsSelected.length == 0 && this.strengths.length == 0,
          message: 'Favor de añadir información de Fortalezas.',
        },
        need: {
          invalid: this.needsSelected.length == 0 && this.needs.length == 0,
          message: 'Favor de añadir  información de Necesidades.',
        },
        goals: {
          invalid: !this.checkedGoals,
          message: 'Favor de añadir información de metas y objetivos.',
        },
        criteria: {
          invalid: !this.criterias?.length,
          message: 'Favor escriba los criterios y procedimientos.',
        },
        specialist: this.specialistErrors,
        document: {
          invalid:
            this.document.documentId == null &&
            !this.documentSaved &&
            this.isPlanBilling,
          message:
            'Para poder facturar el Plan de Intervención el documento firmado es requerido.',
        },
      };
    },
    specialistErrors() {
      return {
        invalid:
          this.specialistInfo.serviceSpecialist == null ||
          this.specialistInfo.writerSpecialist == null,
        message:
          'Favor añadir la información relacionada a los especialistas correspondiente.',
      };
    },
    executionFormData() {
      return {
        executionLevels: this.executionLevelArchieved,
        period: this.avaliablePeriods,
      };
    },
    onlyShowPlan() {
      return Boolean(this.$route.params.onlyShowPlan);
    },
    // JSF: even though if the user is editing, the save ahd submit buttons shouldn't
    // appear if the invoice plan is already submitted.
    showSaveButtons() {
      return !this.onlyShowPlan && !this.invoiceContinueEditing;
    },
    canEditAllSections() {
      // JSF: if the user is editing the discution plan and marking the check for invoicing billing as "NO"
      const isEditMode = this.invoiceContinueEditing && !this.isPlanBilling;
      return isEditMode;
    },
    readOnly() {
      return (
        (this.interventionFlags.isBillingSubmitted &&
          !this.interventionFlags.isAmending) ||
        (!this.canEditAllSections &&
          ((this.interventionFlags.isSubmitted &&
            !this.interventionFlags.isAmending) ||
            this.onlyShowPlan))
      );
    },
    showInvoice() {
      if (this.interventionFlags.isAmending) {
        return true;
      }

      const submittedCheck = !this.isSignHubActive
        ? this.interventionFlags.isSubmitted
        : true;
      const activeCheck = this.isSignHubActive ? this.activeInvoicePlan : true;
      const parentCheck = this.isSignHubActive
        ? true
        : !this.interventionFlags.hasParent;
      return (
        (submittedCheck &&
          activeCheck &&
          !this.interventionFlags.isAmending &&
          !this.achievementSummary &&
          !this.onlyShowPlan) ||
        (this.onlyShowPlanExists && parentCheck) ||
        this.interventionFlags.preSubmit
      );
    },
    invoiceReadOnly() {
      return this.isSignHubActive
        ? this.interventionFlags.isSubmitted &&
            this.invoice.isPlanBilling &&
            !this.invoice?.canChangeBill
        : false;
    },
    signatureDocumentReadOnly() {
      return this.isSignHubActive
        ? !!this.documentSaved &&
            this.interventionFlags.isSubmitted &&
            this.selectedMechanismSignPlan
        : false;
    },
    admissionId() {
      return this.$route.params.admissionId;
    },
    academicYearId() {
      return this.$route.params.academicYearId;
    },
    interventionPlanId() {
      return this.$route.params.interventionPlanId;
    },
    dateIntervention() {
      return this.$route.params.dateIntervention;
    },
    onlyShowPlanExists() {
      return !_.isNil(this.$route.params.onlyShowPlan);
    },
    invoiceDetailsBoard() {
      return [
        [
          {
            label: 'SIE:',
            data: this.admissionInfo.studentSieId,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Estudiante:',
            icon: 'fas fa-address-book',
            data: this.admissionInfo.fullName,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Edad:',
            icon: 'fas fa-calendar-edit',
            data: `${this.admissionInfo.studentAge} años`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Impedimento:',
            icon: 'fas fa-ballot-check',
            data: this.admissionInfo.impediment,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Región:',
            icon: 'fas fa-map-marker-alt',
            data: this.admissionInfo.region,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Municipio:',
            icon: 'fas fa-map-marker-alt',
            data: this.admissionInfo.municipality,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Código-Escuela:',
            icon: 'fas fa-graduation-cap',
            data: this.admissionInfo.place,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Nombre de Especialista:',
            data: this.admissionInfo.serviceSpecialist
              ? this.admissionInfo.serviceSpecialist.name
              : '',
            cols: 'col-4 col-sm col-12 mt-3',
          },
        ],
        [
          {
            label: 'Tipo de Servicio:',
            data: this.admissionInfo.disciplineName,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Modalidad:',
            data: this.admissionInfo.modalityTypeName,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Frecuencia:',
            data: this.admissionInfo.frequency,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Duración:',
            data: this.admissionInfo.duration,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          this.fromThisAcademicYearForward
            ? {
                label: 'Estatus del PEI:',
                data: this.parsePei,
                cols: 'col-4 col-sm col-12 mt-3',
              }
            : [],
          this.interventionFlags && this.interventionFlags.isSubmitted
            ? {
                label: 'Fecha en que se sometió el Plan:',
                data: this.formatDate(this.date),
                cols: 'col-12 mt-3',
              }
            : [],
        ],
      ];
    },
    pageHeaderConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: this.achievementSummary
          ? 'Informe de Logros'
          : 'Añadir Plan de Intervención',
        routes: [
          'Proveedor',
          'Manejo de Referido',
          this.achievementSummary
            ? 'Informe de Logros'
            : 'Añadir Plan de intervención',
        ],
      };
    },
    dropdownActionItems() {
      return [
        {
          label: 'Documentar Ausencia',
          icon: 'fas fa-file-plus text-green',
          visible: this.showDocumentAbsenceButton,
          click: () => (this.showAbsenceDocumentation = true),
        },
        {
          label: 'Enmendar Plan',
          icon: 'fas fa-files-medical text-green',
          visible: this.showAmendPlanButton,
          click: this.amendPlan,
        },
        {
          label: 'Imprimir',
          icon: 'fas fa-print text-green',
          visible: this.showPrintButton,
          click:
            this.summaryReadOnly && this.routeName === 'achievementSummary'
              ? this.printAchievementSummary
              : this.printInterventionPlan,
        },
        {
          label: 'Imprimir Documento Firmado',
          icon: 'fas fa-print text-green',
          visible: this.showSignedPrintButton,
          click: this.handlePrintSignedDocument,
        },
      ];
    },
    pageHeaderActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: this.handleGoBack,
        },
        {
          text: 'Historial de Plan Enmendado',
          icon: 'fas fa-history',
          variant: 'outline-success',
          visible: this.showAmendedPlanHistoryButton,
          click: this.handleAmendedPlanHistory,
        },
        {
          text: 'Ver Plan',
          icon: 'fas fa-file-search',
          variant: 'outline-success',
          visible: this.showShowPlanButton,
          click: this.showPlan,
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'outline-success',
          visible: this.showSaveButton,
          click: () => this.handleSave(),
        },
        {
          text: 'Discutir Plan',
          icon: 'fas fa-save',
          variant: 'outline-success',
          visible: this.showPlanDiscussionButton,
          click: this.handlePlanDiscussion,
        },
        {
          text: 'Guardar Documento',
          icon: 'fas fa-save',
          variant: 'outline-success',
          visible: this.showSaveDocumentButton && !this.canViewSignPlanTab,
          click: () =>
            this.achievementSummary && this.hasSelectMechanismManuscrito
              ? this.saveAchievementSummaryDocumentHandler()
              : this.saveDocumentPlanHandler(),
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag-alt',
          variant: 'success',
          visible: this.showSubmitButton,
          click: this.submitInterventionHandler,
        },
      ];
    },
    showDocumentAbsenceButton() {
      return (
        !!this.interventionPlanId &&
        !this.onlyShowPlan &&
        !this.showAbsenceDocumentation &&
        this.showIfContainStatus([
          AdmissionStatus.Admitido,
          AdmissionStatus.ServicioRelacionadoActivo,
        ])
      );
    },
    showAmendPlanButton() {
      return (
        (this.showSaveButtons &&
          this.readOnly &&
          !this.achievementSummary &&
          !this.onlyShowPlan &&
          this.userHasPermissions('interventionplan.canedit') &&
          this.canShowDropActionsForSignHubPlan) ||
        this.canShowAmendPlanButton
      );
    },
    showPrintButton() {
      return (
        (this.interventionPlanId != null &&
          this.routeName === 'interventionPlan') ||
        (this.achievementSummary &&
          this.summaryReadOnly &&
          this.routeName === 'achievementSummary') ||
        (!!this.interventionPlanId &&
          this.showIfContainStatus([AdmissionStatus.Asignado]))
      );
    },
    showSignedPrintButton() {
      return this.isInterventionPlanElectronicSigned;
    },
    showDropdownActionsButtons() {
      return (
        this.showDocumentAbsenceButton ||
        this.showAmendPlanButton ||
        this.showPrintButton
      );
    },
    showAmendedPlanHistoryButton() {
      return (
        this.interventionFlags.hasHistory &&
        !this.onlyShowPlan &&
        !this.achievementSummary
      );
    },
    showShowPlanButton() {
      return this.readOnly && this.achievementSummary && !this.onlyShowPlan;
    },
    showSaveButton() {
      const saveDocumentCheck = this.isSignHubActive
        ? !this.showSaveDocumentButton
        : true;

      const readOnlyButCanSave =
        this.readOnly && this.planHasDiscussedAndSubmitted;

      return (
        (this.showSaveButtons &&
          (!this.readOnly || readOnlyButCanSave) &&
          saveDocumentCheck) ||
        this.achievementSummary
      );
    },
    showPlanDiscussionButton() {
      if (this.isAmmendedPlan) {
        return false;
      }
      if (
        !!this.interventionPlanId &&
        this.isSignHubActive &&
        this.showIfContainStatus([AdmissionStatus.Asignado])
      ) {
        return false;
      }
      return (
        !this.onlyShowPlan &&
        this.isSignHubActive &&
        (!this.fromThisAcademicYearForward || this.isPeiSigned) &&
        !this.activeInvoicePlan &&
        !!this.interventionPlanId &&
        !this.achievementSummary
      );
    },
    showSaveDocumentButton() {
      return this.isSignHubActive
        ? (this.achievementSummary &&
            this.canShowSaveDocumentAction &&
            !this.documentSaved) ||
            (this.hasSelectMechanismManuscrito &&
              this.hasPlanDiscussion &&
              !this.onlyShowPlan &&
              !this.interventionFlags.isAmending &&
              (!this.interventionFlags.isSubmitted ||
                (this.interventionFlags.isSubmitted && !this.documentSaved)))
        : (this.getIsPlanBilling || this.editingDocument) &&
            !this.onlyShowPlan &&
            this.interventionFlags.isSubmitted &&
            this.userHasPermissions('interventionplan.canchangedocument');
    },
    isPlanInProcessAfterSubmit() {
      return (
        this.interventionFlags.isBillingSubmitted &&
        !this.interventionFlags.isSubmitted &&
        !this.hasPlanDiscussion
      );
    },
    showSubmitButton() {
      if (
        !this.achievementSummary &&
        this.interventionFlags.canSubmitInterventionPlan === false
      ) {
        return false;
      }

      if (this.isPlanInProcessAfterSubmit) {
        return true;
      }

      if (this.isAmmendedPlan && !this.interventionFlags.isSubmitted) {
        return true;
      }

      const hasPlanDiscussionCheck = this.isSignHubActive
        ? this.hasPlanDiscussion
        : true;
      const isAmendingCheck = this.isSignHubActive
        ? this.interventionFlags.isAmending
        : false;
      const showInvoiceCheck = this.isSignHubActive ? true : !this.showInvoice;
      const hasDocumentFileCheck = this.isSignHubActive
        ? (this.selectedMechanismSignPlan !== null &&
            (this.document?.filename || this.documentSaved)) ||
          this.selectedMechanismSignPlan === null
        : true;

      return (
        !!this.interventionPlanId &&
        !this.readOnly &&
        hasPlanDiscussionCheck &&
        showInvoiceCheck &&
        !isAmendingCheck &&
        this.showIfContainStatus([
          AdmissionStatus.Admitido,
          AdmissionStatus.ServicioRelacionadoActivo,
          AdmissionStatus.NoVigenteEgreso,
          AdmissionStatus.NoVigenteAlta,
          AdmissionStatus.NoVigenteCambioServicio,
          AdmissionStatus.Asignado,
        ]) &&
        this.ifFuturePlanAndCanSignPei &&
        hasDocumentFileCheck
      );
    },
    isFutureInterventionPlan() {
      return this.academicYearId == this.futureAcademicYear.id;
    },
    ifFuturePlanAndCanSignPei() {
      if (!this.isFutureInterventionPlan) return true;

      const canSignPeiDate = moment(
        this.getSysParemeterStaticValue(sysparameterByNames.CanSignPeiDate)
      );
      const planShutdownDate = moment(
        this.getSysParemeterStaticValue(sysparameterByNames.PlanShutdownDate)
      );

      var currentDate = moment().format('YYYY/MM/DD');

      const todayIsAfterCanSignPeiDate = moment(currentDate).isSameOrAfter(
        canSignPeiDate
      );
      const todayIsBeforePlanShutdownDate = moment(currentDate).isSameOrBefore(
        planShutdownDate
      );

      return todayIsAfterCanSignPeiDate && todayIsBeforePlanShutdownDate;
    },
    disciplineTypeId() {
      return this.admissionInfo?.disciplineTypeId;
    },
    isActiveServiceRelatedReferral() {
      return (
        this.admissionInfo?.admissionStatusId ===
        AdmissionStatus.ServicioRelacionadoActivo
      );
    },
    isAdmittedReferral() {
      return this.admissionInfo?.admissionStatusId === AdmissionStatus.Admitido;
    },
    academicYearAchievementSummarySignHubAllow() {
      const value =
        this.getSysParemeterStaticValue(
          sysparameterByNames.AcademicYearAchievementSummarySignHubAllow
        ) ?? '31';
      return Number(value);
    },
    isAcademicYearSignHubAllow() {
      return (
        this.academicYearId < this.academicYearAchievementSummarySignHubAllow
      );
    },
    isSignHubActive() {
      if (this.achievementSummary) {
        if (!this.lastAchievement || this.isAcademicYearSignHubAllow) {
          return false;
        }
        return (
          this.getSysParemeterStaticValue(sysparameterByNames.ActiveSignHub) ===
          'true'
        );
      }
      if (
        this.interventionPlanId &&
        this.interventionFlags.isSubmitted &&
        this.selectedMechanismSignPlan === null &&
        this.isAcademicYearSignHubAllow
      ) {
        return false;
      }
      return (
        this.getSysParemeterStaticValue(sysparameterByNames.ActiveSignHub) ===
        'true'
      );
    },
    canViewDocumentSection() {
      const { preSubmit, isSubmitted } = this.interventionFlags;
      return (
        !(this.achievementSummary && !this.isAcademicYearSignHubAllow) &&
        !this.isSignHubActive &&
        (preSubmit || isSubmitted)
      );
    },
    canViewSignPlanTab() {
      return (
        this.isSignHubActive &&
        this.interventionPlanId &&
        ((this.achievementSummary && this.isAchievementSummaryAdmittedStatus) ||
          (!this.achievementSummary &&
            this.isInterventionPlanAdmittedStatus &&
            !this.interventionFlags.isAmending &&
            this.hasPlanDiscussion &&
            (this.hasPlanDiscussion ||
              this.interventionFlags.isBillingSubmitted)))
      );
    },
    canCompleteSelectedMechanism() {
      return this.canViewSignPlanTab;
    },
    hasSelectMechanismManuscrito() {
      return this.selectedMechanismSignPlan === false;
    },
    hasSelectMechanismElectronica() {
      return this.selectedMechanismSignPlan === true;
    },
    isElectronicSignature() {
      return this.hasSelectMechanismElectronica
        ? true
        : this.hasSelectMechanismManuscrito
        ? false
        : null;
    },
    isInterventionPlanElectronicSigned() {
      return (
        this.signatureHubInfo?.documentInfo?.documentStatusId ===
        DigitalSignatureDocumentStatus.Signed
      );
    },
    interventionPlanElectronicSignedDocumentId() {
      return this.signatureHubInfo?.documentId;
    },
    signPlanRefDisabled() {
      const hasDocumentStatus = !!this.signatureHubInfo?.documentInfo
        ?.documentStatusId;
      const manuscritoReady =
        this.hasSelectMechanismManuscrito &&
        this.hasSignDocument &&
        !this.invoice?.canChangeBill;
      if (!this.achievementSummary) {
        return (
          (this.interventionFlags.isBillingSubmitted &&
            this.interventionFlags.isSubmitted &&
            !this.invoice?.canChangeBill) ||
          hasDocumentStatus ||
          manuscritoReady ||
          (!hasDocumentStatus &&
            this.interventionFlags.isSubmitted &&
            !this.invoice?.canChangeBill)
        );
      }
      return this.signatureHubProcessDisabled;
    },
    signatureHubProcessDisabled() {
      return (
        !!this.signatureHubInfo?.documentInfo?.documentStatusId ||
        (this.hasSelectMechanismManuscrito && this.hasSignDocument)
      );
    },
    signatureHubDocumentStatusId() {
      return (
        this.signatureHubInfo?.documentInfo?.documentStatusId ??
        DigitalSignatureDocumentStatus.DocNotGenerated
      );
    },
    signatureHubDocumentStatusName() {
      return (
        this.signatureHubInfo?.documentInfo?.documentStatus ??
        'Documento no Generado'
      );
    },
    signatureHubDocumentStatusColor() {
      return DigitalSignatureDocumentStatusColor[
        this.signatureHubDocumentStatusId
      ];
    },
    canShowSaveDocumentAction() {
      return !this.isSignHubActive || this.hasSelectMechanismManuscrito;
    },
    canShowDropActionsForSignHubPlan() {
      return (
        !this.isSignHubActive ||
        (this.hasSelectMechanismManuscrito && this.hasSignDocument) ||
        this.hasSelectMechanismElectronica
      );
    },
    canShowAmendPlanButton() {
      const canShowResult =
        this.userHasPermissions('interventionplan.canedit') &&
        this.isSignHubActive &&
        this.interventionFlags.isSubmitted &&
        !this.interventionFlags.isAmending;
      return canShowResult;
    },
    isAchievementSummaryAdmittedStatus() {
      return (
        this.achievementSummary === true &&
        [
          AdmissionStatus.ServicioRelacionadoActivo,
          AdmissionStatus.NoVigenteEgreso,
          AdmissionStatus.NoVigenteCambioServicio,
        ].includes(this.admissionInfo?.admissionStatusId)
      );
    },
    isInterventionPlanAdmittedStatus() {
      return (
        this.achievementSummary === false &&
        [
          AdmissionStatus.Admitido,
          AdmissionStatus.ServicioRelacionadoActivo,
          AdmissionStatus.Asignado,
          AdmissionStatus.NoVigenteEgreso,
          AdmissionStatus.NoVigenteAlta,
          AdmissionStatus.NoVigenteCambioServicio,
        ].includes(this.admissionInfo?.admissionStatusId)
      );
    },
    lastAchievement() {
      if (
        Array.isArray(this.avaliablePeriods) &&
        Array.isArray(this.archievements) &&
        this.archievements.length > 0 &&
        this.avaliablePeriods.length > 0
      ) {
        const sortedPeriods = this.avaliablePeriods
          .slice()
          .sort((a, b) => b.order - a.order);
        const lastMatchedAchievement = sortedPeriods
          .map((period) => {
            const achievement = this.archievements.find(
              (a) =>
                a.period.periodId === period.periodId &&
                a.period.academicYearId === period.academicYearId
            );
            return { achievement };
          })
          .find((match) => match.achievement);
        if (lastMatchedAchievement) {
          const { achievement } = lastMatchedAchievement;
          return achievement;
        }
      }
      return null;
    },
    lastAchievementSignatureTypeId() {
      switch (this.lastAchievement?.period?.periodId) {
        case interventionPlanPeriods.december:
          return signatureHubTypes.ACHIEVEMENT_INFORM_DECEMBER;
        case interventionPlanPeriods.may:
          return signatureHubTypes.ACHIEVEMENT_INFORM_MAY;
        case interventionPlanPeriods.june:
          return signatureHubTypes.ACHIEVEMENT_INFORM_JUNE;
      }
      return null;
    },
    interventionPlanAchievementId() {
      return this.lastAchievement?.id;
    },
    signatureTypeId() {
      return this.achievementSummary === true
        ? this.lastAchievementSignatureTypeId
        : signatureHubTypes.INTERVENTION_PLAN;
    },
    relatedId() {
      return this.achievementSummary === true
        ? this.interventionPlanAchievementId
        : this.interventionPlanId;
    },
    signatureHubFrom() {
      return this.achievementSummary === true
        ? 'achievementSummary'
        : 'interventionPlan';
    },
    questionLabel() {
      return this.achievementSummary === true
        ? '¿Cuál será el mecanismo que se estará utilizando para firmar el Informe de Logros?'
        : '¿Cuál será el mecanismo que se estará utilizando para firmar el Plan de Intervención?';
    },
    signatureDocumentTitle() {
      return this.achievementSummary === true
        ? `Informe de Logros - ${this.monthName} Firmado`
        : 'Plan de Intervención Firmado';
    },
    achievementDocumentTypeId() {
      switch (this.lastAchievement?.period?.periodId) {
        case interventionPlanPeriods.december:
          return documentTypes.InformedeLogrosDiciembre;
        case interventionPlanPeriods.may:
          return documentTypes.InformedeLogrosMayo;
        case interventionPlanPeriods.june:
          return documentTypes.InformedeLogrosJunio;
      }
      return null;
    },
    monthName() {
      switch (this.lastAchievement?.period?.periodId) {
        case interventionPlanPeriods.december:
          return 'Diciembre';
        case interventionPlanPeriods.may:
          return 'Mayo';
        case interventionPlanPeriods.june:
          return 'Junio';
      }
      return '';
    },
    parsePei() {
      if (this.lastPei == null || this.lastPei.peiId == null) return '-';

      return (
        this.lastPei.peiId.slice(0, 4) +
        '-' +
        this.lastPei.peiId.slice(4, 10) +
        ' - ' +
        this.lastPei.peiStatusDescription
      );
    },
    showAreaSelection() {
      return (
        this.getSysParemeterStaticValue(
          sysparameterByNames.InverteventionPlanAreaSelection
        ) === 'true'
      );
    },
    planHasDiscussedAndSubmitted() {
      return (
        this.hasPlanDiscussion && this.interventionFlags.isBillingSubmitted
      );
    },
    studentHasEducationalService() {
      return this.showAreaSelection && this.areaOptions.length > 0;
    },
  },
  async mounted() {
    const { data: futureAcademicYear } = await _getFutureAcademicYear();
    this.futureAcademicYear = futureAcademicYear;

    const { data: academicYear } = await _getAcademicYear(this.academicYearId);
    this.academicYear = academicYear;

    const { data: sysParameters } = await _getSysparameterByNames({
      parameterNames: [
        sysparameterByNames.CanSignPeiDate,
        sysparameterByNames.PlanShutdownDate,
      ],
      academicYearId: this.futureAcademicYear.id,
    });

    const { data: activeSignHubParameter } = await _getSysparameterByNames({
      parameterNames: [sysparameterByNames.ActiveSignHub],
    });

    const {
      data: academicYearAchievementSummarySignHubAllowParameter,
    } = await _getSysparameterByNames({
      parameterNames: [
        sysparameterByNames.AcademicYearAchievementSummarySignHubAllow,
      ],
    });

    const {
      data: inverteventionPlanAreaSelection,
    } = await _getSysparameterByNames({
      parameterNames: [sysparameterByNames.InverteventionPlanAreaSelection],
      academicYearId: this.academicYearId,
    });

    this.sysParameters = [
      ...sysParameters,
      ...activeSignHubParameter,
      ...academicYearAchievementSummarySignHubAllowParameter,
      ...inverteventionPlanAreaSelection,
    ];

    await this.getAdmissionInfo();

    if (this.showAreaSelection) {
      await this.getLastPeibyStudentsie();
      await this.getInterventionPlanAvailableServiceAreas();
      await this.getInterventionPlanAvailableGoals();
    }

    if (this.interventionPlanId != null) {
      await this.getInterventionPlan();
    }
    if (this.achievementSummary) {
      await this.getCodeExecutionLevelArchieved();
      await this.getCurrentPeriod();
      await this.getAchievementSummaryInfo();
    }

    if (this.$route.params.from) {
      this.from = this.$route.params.from;
      this.fromData = this.$route.params.fromData;
    }

    this.routeName = this.$route.name;

    if (this.specialistInfo.serviceSpecialist == null) {
      this.specialistInfo.serviceSpecialist = this.admissionInfo.serviceSpecialist;
    }

    if (this.specialistInfo.admissionSpecialist == null) {
      this.specialistInfo.admissionSpecialist = this.admissionInfo.admissionSpecialistEntity;
    }

    await this.getCollections();
    await this.getSpecialistInfo();
    this.syncBackup();
  },
  methods: {
    isPlanBillingChange(data) {
      this.isPlanBilling = data;
    },
    async getAdmissionInfo() {
      const { data } = await getStudentReferralAdmision(this.admissionId);
      this.admissionInfo = { ...data };
    },
    async getLastPeibyStudentsie() {
      const { data } = await _getLastPeibyStudentsie(
        this.admissionInfo.studentSieId,
        this.academicYearId
      );
      this.lastPei = { ...data };
    },
    async getInterventionPlanAvailableServiceAreas() {
      const { data } = await _getInterventionPlanAvailableServiceAreas(
        this.admissionInfo.studentSieId,
        this.academicYearId
      );
      this.areaOptions = [...data];
    },
    async getInterventionPlanAvailableGoals() {
      const { data } = await _getInterventionPlanAvailableGoals(
        this.admissionInfo.studentSieId,
        this.academicYearId
      );
      this.goalOptions = [...data];
    },
    async getInterventionPlan(planId, isParent = false) {
      this.isLoadingPlanInfo = true;
      await getInterventionPlan(planId || this.interventionPlanId).then(
        async (res) => {
          const data = res.data;
          this.date = data.date;
          const interventionFlags = {
            ...this.interventionFlags,
            isSubmitted: data.isSubmitted,
            isBillingSubmitted: data.isBillingSubmitted,
            hasHistory: data.hasHistory,
            hasParent: data.hasParent,
            parentPlanId: data.parentPlanId,
            canSubmitInterventionPlan: data.canSubmitInterventionPlan,
          };

          this.interventionFlags = { ...interventionFlags };

          const invoice = {
            ...this.invoice,
            isPlanBilling: data.isPlanBilling,
            date: data.invoiceDate,
            endTime: data.visitTimeEnd
              ? moment()
                  .set({
                    hour: data.visitTimeEnd?.hours,
                    minute: data.visitTimeEnd?.minutes,
                  })
                  .format('HH:mm zz')
              : null,
            startTime: data.visitTimeStart
              ? moment()
                  .set({
                    hour: data.visitTimeStart?.hours,
                    minute: data.visitTimeStart?.minutes,
                  })
                  .format('HH:mm zz')
              : null,
            canChangeBill: data.canChangeBill,
            billNumber: data.billNumber,
            referralOverrideSpecialist:
              data.referralOverrideSpecialist ??
              this.admissionInfo.serviceSpecialist,
          };

          this.invoice = { ...invoice };

          // si es enmienda
          if (
            !isParent &&
            // si el plan aun no ha sido guardado
            invoice.isPlanBilling === null &&
            this.interventionFlags.hasParent &&
            this.interventionFlags.parentPlanId != null &&
            !this.interventionFlags.isBillingSubmitted
          ) {
            await this.getInterventionPlan(
              this.interventionFlags.parentPlanId,
              true
            );

            // Keep the same value of current plan... the rest of value will be the parent plan values.
            this.interventionFlags.isBillingSubmitted = data.isBillingSubmitted;
            this.interventionFlags.isSubmitted = data.isSubmitted;
            this.invoice.isPlanBilling = data.canChangeBill;
          }

          this.criterias =
            data.criteriaAndProcedures.length > 0
              ? data.criteriaAndProcedures[0].description
              : '';

          this.goals = data.goals.map((g) => ({
            ...g,
            objectives: g.objectives.map((o) => ({
              ...o,
              executionLevelArchieved: o.archievements
                .map((a) => ({
                  executionLevel: a.executionLevelArchieved,
                  period: this.mapPeriod(a.period),
                }))
                .sort((a, b) => a.period.order - b.period.order),
            })),
          }));

          this.strengths = data.strengths.map((s) => ({
            id: s.id,
            description: s.description,
          }));

          this.needs = data.needs.map((s) => ({
            id: s.id,
            description: s.description,
          }));

          this.archievements = data.archievements
            .map((a) => ({
              achievement: a.description,
              sessions: a.sessions,
              id: a.id,
              period: this.mapPeriod(a.period),
              specialist: a.specialist || { name: '' },
              isElectronicSignature: a.isElectronicSignature,
            }))
            .sort((a, b) => a.period.order - b.period.order);

          this.summaryReadOnly = Boolean(this.archievements.length);
          this.specialistInfo.admissionSpecialist = data.admissionSpecialist;
          this.specialistInfo.serviceSpecialist = data.serviceSpecialist;
          this.specialistInfo.writerSpecialist = data.writerSpecialist;

          if (data.document != null) {
            this.document = {
              name: data.document.filename,
              documentType: data.document.documentTypeId,
              documentId: data.document.documentId,
              readOnly: true,
              inServer: true,
              file: null,
            };
            this.documentOriginal = { ...this.document };
            this.documentSaved = true;
            this.hasSignDocument = !!data.document.documentId;
          } else {
            if (!this.document) {
              this.document = { documentId: null, name: null };
            }
          }

          this.hasDocumentFile = false;

          this.submittedSummaries = data.archievements.map((x) => ({
            period: x.period.value,
            academicYearId: x.academicYear.id,
          }));

          this.signatureHubInfo = null;
          this.selectedMechanismSignPlan =
            data.isElectronicSignature == null && data.document != null
              ? false
              : data.isElectronicSignature;
          if (this.isSignHubActive && !this.achievementSummary) {
            this.activeInvoicePlan = !_.isNil(this.invoice.isPlanBilling);
            this.hasPlanDiscussion = this.activeInvoicePlan;
            if (data.isElectronicSignature) {
              this.getSignatureHubInfo();
            }
          }
        }
      );

      this.syncBackup();
      this.isLoadingPlanInfo = false;
    },
    mapPeriod(period) {
      const id = Number(period.value);
      const periodName =
        period.name.charAt(0).toUpperCase() + period.name.slice(1);
      let mappedObj = {
        name: `${periodName}`,
        periodId: id,
        academicYearId: period.id,
      };
      switch (id) {
        case interventionPlanPeriods.december:
          mappedObj.order = 1;
          break;
        case interventionPlanPeriods.may:
          mappedObj.order = 2;
          break;
        case interventionPlanPeriods.june:
          mappedObj.order = 3;
          break;
      }
      return mappedObj;
    },
    async getCurrentPeriod() {
      const { data } = await getCurrentPeriod();
      this.avaliablePeriods = data.periods.map((p) => {
        return this.mapPeriod(p);
      });
    },
    async getCodeExecutionLevelArchieved() {
      const { data } = await getCodeExecutionLevelArchieved();
      this.executionLevelArchieved = data;
    },
    async amendPlan() {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea enmendar el plan de intervención?`,
        'Si',
        'No'
      );
      if (!answer) return;
      this.interventionFlags.isAmending = true;
      if (this.isSignHubActive) {
        this.document = { documentId: null, name: null };
        this.documentSaved = false;
        this.hasSignDocument = false;
      }
    },
    showPlan(id = null) {
      this.routeName = 'interventionPlan';
      this.$router.push({
        name: this.routeName,
        params: {
          admissionId: this.admissionId,
          academicYearId: this.academicYearId,
          interventionPlanId: id ?? this.interventionPlanId,
          onlyShowPlan: id ? undefined : Number(true),
        },
      });
    },
    async handleSave() {
      if (this.achievementSummary) {
        await this.submitInterventionPlanSummary(true);
      } else {
        if (this.planHasDiscussedAndSubmitted) {
          await this.submitInterventionHandler();
        } else {
          await this.submitInterventionPlan(true);
        }
      }
    },
    async confirmSubmit() {
      return this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea someter el plan de intervención del estudiante <b>${this.admissionInfo.fullName}</b>?`,
        'Si',
        'No'
      );
    },
    async submitInterventionHandler(bypassConfirm = false) {
      this.validate = true;
      const fail = (title, msg, tab = 1) => {
        this.ShowToast(title, msg, 'error');
        this.tabModel = tab;
        return false;
      };
      const documentInvalidCheck =
        this.isSignHubActive && !this.achievementSummary
          ? false
          : this.errors.document.invalid;
      if (
        this.errors.goals.invalid ||
        this.errors.criteria.invalid ||
        documentInvalidCheck ||
        this.errors.specialist.invalid ||
        this.errors.strength.invalid ||
        this.errors.need.invalid
      ) {
        this.validationToast();
        return;
      }

      const parentOrAmend =
        this.interventionFlags.isAmending || this.interventionFlags.hasParent;
      if (this.isSignHubActive) {
        if (
          !this.achievementSummary &&
          this.isPlanBilling &&
          this.invoice.canChangeBill &&
          !this.interventionFlags.hasParent
        ) {
          if (!(await this.$refs?.signPlanRef?.validate())) {
            return fail(
              'Información Incompleta',
              'Favor seleccionar el mecanismo de firma del Plan de Intervención.'
            );
          }
          if (
            !this.signatureHubProcessDisabled ||
            (this.hasSelectMechanismElectronica &&
              !this.isInterventionPlanElectronicSigned)
          ) {
            return fail(
              'Alerta',
              'Debe existir un documento firmado para poder someter.'
            );
          }
          if (await this.confirmSubmit()) {
            await this.submitInterventionPlan();
            return;
          }
          return;
        }
        if (!this.achievementSummary && !this.isPlanBilling) {
          if (this.hasSelectMechanismElectronica) {
            if (this.isInterventionPlanElectronicSigned) {
              if (await this.confirmSubmit()) {
                await this.submitInterventionPlan();
                return;
              }
            }
            return fail(
              'Alerta',
              'Debe existir un documento firmado para poder someter.'
            );
          }
          if (await this.confirmSubmit()) {
            await this.submitInterventionPlan();
            return;
          }
          return;
        }
        if (parentOrAmend && !this.interventionFlags.hasParent) {
          if (!(await this.$refs?.signPlanRef?.validate())) {
            return fail(
              'Información Incompleta',
              'Favor seleccionar el mecanismo de firma del Plan de Intervención.'
            );
          }
          if (
            !this.signatureHubProcessDisabled ||
            (this.hasSelectMechanismElectronica &&
              !this.isInterventionPlanElectronicSigned)
          ) {
            return fail(
              'Alerta',
              'Debe existir un documento firmado para poder someter.'
            );
          }
        }
      }

      if (
        parentOrAmend ||
        (!this.achievementSummary &&
          this.isPlanBilling &&
          !this.invoice.canChangeBill)
      ) {
        if (bypassConfirm || (await this.confirmSubmit())) {
          await this.submitInterventionPlan();
        }
        return;
      }

      this.interventionFlags.preSubmit = true;
      this.editMode(true);
    },
    async editMode(value) {
      this.invoiceContinueEditing = value;
    },
    syncBackup() {
      const tabModelValue = this.$route.params.tabModel;
      this.dataBackup = {
        invoice: { ...this.invoice, isPlanBilling: this.isPlanBilling },
        isPlanBilling: this.isPlanBilling,
        goals: this.goals.slice(),
      };
      if (tabModelValue) {
        this.tabModel = tabModelValue;
        this.selectedMechanismSignPlan = true;
        this.getSignatureHubInfo();
      }
    },
    resetData() {
      this.invoice = { ...this.dataBackup.invoice };
      this.isPlanBilling = this.dataBackup.isPlanBilling;
      this.goals = this.dataBackup.goals.slice();
    },
    async cancelHandler() {
      this.resetData();
      this.interventionFlags.preSubmit = false;
      if (this.activeInvoicePlanByAction) {
        this.activeInvoicePlan = false;
        this.activeInvoicePlanByAction = false;
      }
    },
    async alertBillingChangeHandler() {
      if (!this.invoice.billNumber && this.interventionPlanId != null) {
        await this.getInterventionPlan();
        this.$refs.invoicePlan.editMode(true);
        return;
      }
      this.ShowToast(
        'Alerta',
        `No es posible modificar la fecha, ya que se encuentra en una factura sometida: ${this.invoice.billNumber}.`,
        'error'
      );
    },
    async invoiceSaveHandler(info) {
      this.validate = true;
      if (
        (!this.isSignHubActive && this.errors.document.invalid) ||
        (!this.achievementSummary &&
          (this.errors.goals.invalid ||
            this.errors.criteria.invalid ||
            this.errors.specialist.invalid))
      ) {
        this.validationToast();
        this.invoiceContinueEditing = true;
        return;
      }

      if (
        (this.isAdmittedReferral || this.isActiveServiceRelatedReferral) &&
        info?.invoice?.id === OPTION_BILLING_PLAN_DISCUSSION_YES
      ) {
        const payload = {
          entryDate: info.date,
          flagType: ConfRelatedAcademicWeekFlagTypes.InterventionPlanFlag,
        };
        const { data: isActiveDayOfWeek } = await _validateIsActiveDayOfWeek(
          payload
        );
        if (!isActiveDayOfWeek) {
          this.ShowToast('Atención', 'Dia de visita no es lectivo', 'error');
          return;
        }
      }
      // JSF: we use a variable instead of continue using the computed because
      // durint this method the edit mode change to false.
      const editingAllSections = this.canEditAllSections;
      // JSF: if can edit all section, then it should save those info first.
      if (editingAllSections) {
        if (this.achievementSummary) {
          await this.submitInterventionPlanSummary();
        } else {
          if (!this.isSignHubActive) {
            const bypassConfirmationMsg = true;
            await this.submitInterventionHandler(bypassConfirmationMsg);
          } else {
            await this.submitInterventionPlan(true);
          }
        }
      }

      this.validate = false;
      this.invoiceContinueEditing = false;
      this.invoice.date = info.date;
      this.invoice.endTime = info.endTime;
      this.invoice.startTime = info.startTime;
      const params = objectToFormData(
        {
          id: Number(this.interventionPlanId),
          invoiceDate: info.date,
          isPlanBilling: info.invoice.value,
          endTime: info.endTime,
          startTime: info.startTime,
          admissionId: this.admissionId,
          referralOverrideSpecialistId: info.referralOverrideSpecialist?.id,
        },
        { indices: true }
      );

      if (this.document && this.document.file && !this.document.documentId) {
        params.append(`document`, this.document.file, this.document.file.name);
      }

      if (this.interventionFlags.preSubmit) {
        // JSF: if we are editing other sections then we bypass the confirm msg.
        if (editingAllSections || (await this.confirmSubmit())) {
          this.invoiceContinueEditing = false;

          if (
            this.isFutureInterventionPlan ||
            this.invoice.date > new Date(this.dateIntervention) ||
            !info.invoice.value ||
            !this.dateIntervention
          ) {
            await this.submitInvoice(params).then(
              async () => await this.submitInterventionPlan()
            );
          } else {
            this.invoiceContinueEditing = true;
            this.ShowToast(
              'Error',
              `No se identificó vigencia de servicio para el día de visita. `,
              'error'
            );
          }
        } else {
          this.invoiceContinueEditing = true;
        }

        this.syncBackup();
        return;
      }

      this.syncBackup();
      await this.submitInvoice(params);

      const needToReloadPlanInfo =
        (this.isSignHubActive && !this.achievementSummary) ||
        (this.interventionFlags.hasParent &&
          this.interventionFlags.parentPlanId != null &&
          this.interventionFlags.isBillingSubmitted);
      if (needToReloadPlanInfo) {
        await this.getInterventionPlan();
      }
    },
    async submitInvoice(params) {
      await savePlanDiscussedWithParentsFlag(params)
        .then(() => {
          this.invoiceContinueEditing = false;
          this.ShowToast(
            'Confirmación',
            `La información ha sido guardada exitosamente.`,
            'success'
          );
        })
        .catch((err) => {
          if (err.response != null) {
            this.invoiceContinueEditing = true;
            this.ShowToast(
              'Alerta',
              err.response.data.message || 'error',
              'error',
              5000
            );
            this.invoice.date = null;
            throw new Error(err.response.data.message || 'error');
          }
        });
    },
    async submitInterventionPlan(isSave = false) {
      const paramGoals = this.goals.map((g) => {
        const goal = { ...g };
        delete goal.id;
        delete goal.editGoal;
        goal.objectives = goal.objectives.map((o) => {
          const measurement = o.measurement?.id || o.measureCodeId;
          return {
            description: o.description,
            measureCodeId: measurement,
            measureAmt1: Number(o.percent) || Number(o.measureAmt1),
            measureAmt2:
              measurement != interventionPlanMeasure.Percent
                ? Number(o.measureAmt2)
                : 0,
          };
        });
        return goal;
      });
      const params = {
        id: this.interventionPlanId ? Number(this.interventionPlanId) : null,
        studentSieId: this.admissionInfo.studentSieId,
        academicYearId: Number(this.academicYearId),
        studentServiceId: this.admissionInfo.studentServiceId,
        isSubmitted: !isSave,
        isAmendment: this.interventionFlags.isAmending,
        goals: paramGoals,
        strengths: this.strengthsSelected.map((s) => ({ description: s.name })),
        needs: this.needsSelected.map((s) => ({ description: s.name })),
        criteriaAndProcedures: [{ description: this.criterias }],
        admissionSpecialistId: this.specialistInfo.admissionSpecialist?.id,
        serviceSpecialistId: this.specialistInfo.serviceSpecialist?.id,
        writerSpecialistId: this.specialistInfo.writerSpecialist?.id,
        isElectronicSignature: this.isElectronicSignature,
        isSavingSubmittedPlan: this.planHasDiscussedAndSubmitted,
      };

      if (
        this.specialistErrors.invalid &&
        !(isSave && this.studentHasEducationalService)
      ) {
        this.ShowToast(
          'Alerta',
          `Se requiere completar la información de especialistas.`,
          'error'
        );
        return;
      }

      await this.addInterventionPlan(params);
      this.syncBackup();
    },
    async addInterventionPlan(params) {
      await submitInterventionPlan(params)
        .then(({ data }) => {
          let action = params.isSubmitted ? 'sometido' : 'guardado';
          this.ShowToast(
            'Confirmación',
            `El plan de intervención ha sido ${action} exitosamente.`,
            'success'
          );

          this.interventionFlags = {
            ...this.interventionFlags,
            isSubmitted: params.isSubmitted,
            isAmending: false,
            preSubmit: false,
          };

          if (data) {
            this.showPlan(data);
            this.getInterventionPlan(data);
          }
        })
        .catch((err) => {
          if (err.response != null)
            this.ShowToast(
              'Alerta',
              err.response.data.message || 'error',
              'error',
              5000
            );
        });
    },
    async submitInterventionPlanSummary(isSave = true) {
      this.partialSave = isSave;
      this.validate = true;
      let objectiveArchievements = [];

      this.goals.forEach((goal) => {
        goal.objectives.forEach((objective) => {
          objectiveArchievements = objectiveArchievements.concat(
            (objective.executionLevelArchieved || []).map((o) => ({
              objectiveId: objective.id,
              executionLevelArchievedId: o.executionLevel.id,
              period: o.period.periodId,
              academicYearId: o.period.academicYearId,
            }))
          );
        });
      });

      if (
        (this.summaryErrors().achievement.invalid && !isSave) ||
        this.summaryErrors(objectiveArchievements.length).executionLevel.invalid
      ) {
        this.validationToast();
        return;
      }
      this.validate = false;

      if (!isSave) {
        const answer = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro que desea someter el resumen de logros del estudiante <b>${this.admissionInfo.fullName}</b>?`,
          'Si',
          'No'
        );
        if (!answer) return;
      }

      const params = objectToFormData(
        {
          objectiveArchievements,
          archievements: isSave
            ? null
            : this.archievements.map((a) => ({
                description: a.achievement,
                sessions: Number(a.sessions),
                period: a.period.periodId,
                academicYearId: a.period.academicYearId,
                specialistId: a.specialist.id,
              })),
          admissionId: this.admissionId,
          isUsingSignatureHub: this.hasSelectMechanismManuscrito ?? false,
        },
        { indices: true }
      );

      if (
        !isSave &&
        this.document &&
        this.document.file &&
        !this.document.documentId
      ) {
        params.append(`document`, this.document.file, this.document.file.name);
        if (this.achievementDocumentTypeId) {
          params.append(`documentTypeId`, `${this.achievementDocumentTypeId}`);
        }
      }

      await this.addInterventionPlanSummary(params, isSave);
      await this.getInterventionPlan(this.interventionPlanId);
      await this.getAchievementSummaryInfo();
    },
    async addInterventionPlanSummary(params, isSave) {
      await submitInterventionPlanSummary(this.interventionPlanId, params)
        .then(() => {
          let action = isSave ? 'guardado' : 'sometido';
          this.ShowToast(
            'Confirmación',
            'El plan de intervención ha sido ' + action + ' exitosamente.',
            'success'
          );
          this.summaryReadOnly = !isSave;
          this.submittedSummaries = this.archievements.map((x) => ({
            period: x.period.periodId,
            academicYearId: x.period.academicYearId,
          }));
        })
        .catch((err) => {
          if (err.response != null)
            this.ShowToast(
              'Alerta',
              err.response.data.message || 'error',
              'error',
              5000
            );
        });
    },
    async saveGoalSummaryHandler(achievement) {
      const idx = this.archievements.findIndex((a) => a.id == achievement.id);
      if (idx >= 0) {
        this.archievements.splice(idx, 1, achievement);
      } else {
        this.archievements.push(achievement);
        this.archievements.sort((a, b) => a.period.order - b.period.order);
      }
      this.submitInterventionPlanSummary(false);
    },
    async deleteArchivement(id) {
      const response = await deleteArchivement(id).catch((error) => {
        this.ShowToast('', error.response.data.message, 'error');
      });
      if (response) {
        const idx = this.archievements.findIndex((a) => a.id == id);
        if (idx >= 0) {
          this.submittedSummaries.splice(
            this.submittedSummaries.findIndex(
              (s) => s.period == this.archievements[idx].period.periodId
            ),
            1
          );
          this.archievements.splice(idx, 1);
          this.ShowToast(
            'Confirmación',
            `El Resumen de Logro ha sido eliminado exitosamente.`,
            'success'
          );
          this.document = {};
          this.hasSignDocument = false;
        }
      }
    },
    deleteGoalsSummaryHandler(id) {
      const idx = this.archievements.findIndex((a) => a.id == id);
      this.archievements.splice(idx, 1);
    },
    changeDataHandler(goal) {
      const index = this.goals.findIndex((g) => g.id == goal.id);
      this.goals[index] = goal;
      localStorage.setItem('goals', JSON.stringify(this.goals));
      this.goals = JSON.parse(localStorage.getItem('goals'));
    },
    async saveCriteriaHandler(criteria) {
      const isValid = await this.$refs.interventionPlanCriterias.validate();
      if (!isValid) {
        this.validationToast();
        return;
      }
      this.criterias = criteria;
    },
    async savestrengthsHandler(strengths) {
      let element = document.getElementById('collapse-item-strength');
      element.lastElementChild.classList.remove('show');
      this.strengthsSelected = strengths;
    },
    async saveNeedsHandler(needs) {
      let element = document.getElementById('collapse-item-need');
      element.lastElementChild.classList.remove('show');
      this.needsSelected = needs;
    },
    async saveDocumentHandler(document, docSaved = true) {
      if (!this.isSignHubActive) {
        const isValid = await this.$refs.interventionPlanDocument.validate();
        if (!isValid) {
          this.documentSaved = false;
          this.validationToast();
          return;
        }
      }
      this.document.file = document;
      this.document.name = document.name;
      this.document.documentId = null;
      this.documentSaved = docSaved;
      this.hasDocumentFile = !!this.document.file;
    },
    async editDocumentHandler() {
      this.documentSaved = false;
      this.editingDocument = true;
    },
    cancelCriteriahandler() {
      let element = document.getElementById('collapse-item-criterion');
      element.lastElementChild.classList.remove('show');
    },
    cancelDocumenthandler(saved) {
      if (!saved) {
        let element = document.getElementById('collapse-item-document');
        element.lastElementChild.classList.remove('show');
      }
      this.editingDocument = false;
      this.documentSaved = saved;
    },
    handleCancelSignatureDocument() {
      this.documentSaved = true;
      if (this.documentOriginal?.documentId) {
        this.document = { ...this.documentOriginal };
      }
      this.hasDocumentFile = false;
    },
    async deleteCriteriaHandler() {
      const answer = await this.ShowCancelModal(
        'Alerta',
        '¿Está seguro que desea eliminar el criterio y procedimiento?',
        'Sí',
        'No'
      );

      if (!answer) return;

      this.criterias = '';
      this.ShowToast(
        'Alerta',
        'El criterio y procedimiento ha sido eliminado exitosamente.',
        'success'
      );
    },
    addGoal() {
      if (this.goals.length == 2) {
        this.ShowToast(
          'Alerta',
          'No es posible añadir más de dos metas por plan de intervención.',
          'warning'
        );
        return;
      }
      this.showAddGoal = true;
    },
    async saveGoalHandler(goal) {
      const isValid = await this.$refs.interventionPlanGoal.validate();
      if (!isValid) {
        this.validationToast();
        return;
      }
      const data = {
        id: Math.random() * 10,
        editGoal: false,
        ...goal,
      };
      this.goals.push(data);
      this.cancelGoalHandler();
    },
    cancelGoalHandler() {
      this.showAddGoal = false;
    },
    async cancelUpdateHandler(index) {
      this.goals[index].editGoal = false;
      await this.$forceUpdate();
    },
    async showEditGoalHandler(index) {
      this.goals.forEach((o) => {
        o.editGoal = false;
      });
      this.goals[index].editGoal = !this.goals[index].editGoal;
      await this.$forceUpdate();
    },
    editGoalHandler(goal, index) {
      this.goals[index] = goal;
      this.cancelUpdateHandler(index);
    },
    deleteGoalHandler(index) {
      this.goals.splice(index, 1);
    },
    validationToast() {
      this.ShowToast(
        'Información Incompleta',
        'Favor completar la información solicitada para poder continuar.',
        'error'
      );
    },
    async handlePrintSignedDocument() {
      const response = await _getSignedDocument(
        this.signatureTypeId,
        this.relatedId,
        this.interventionPlanElectronicSignedDocumentId
      ).catch((error) => {
        this.ShowToast('', error.response.data.message, 'error');
      });
      if (response?.data) {
        _generalApi.ConvertToBlob(response.data);
      }
    },
    async printInterventionPlan() {
      let report = `referral/interventionplanreport/${this.admissionId}/${this.interventionPlanId}`;
      await _generalApi.downloadReport(report);
    },
    async printAchievementSummary() {
      if (this.isSignHubActive && this.lastAchievement?.period?.periodId) {
        let report = `referral/achievementsummaryperiodreport/${this.admissionId}/${this.interventionPlanId}/${this.lastAchievement?.period?.periodId}`;
        await _generalApi.downloadReport(report);
        return;
      }
      let report = `referral/achievementssummaryreport/${this.admissionId}/${this.interventionPlanId}`;
      await _generalApi.downloadReport(report);
    },
    async getCollections() {
      await this.getAdmissionSpecialists();
      await this.getAbsenceReasons();
    },
    async getAdmissionSpecialists() {
      //Admission Specialists
      await getSpecialistsForAssignment(
        this.admissionId,
        this.admissionInfo.locationFiscalYearId,
        this.admissionInfo.studentSieId,
        0,
        true
      ).then((res) => {
        this.collections.admissionSpecialists = res.data;
      });

      //Services Specilists
      await getSpecialistsForAssignment(
        this.admissionId,
        this.admissionInfo.locationFiscalYearId,
        this.admissionInfo.studentSieId,
        0,
        false
      ).then((res) => {
        this.collections.serviceSpecialists = res.data;
      });

      //Plan's Writer
      await getSpecialistList({
        fiscalYearId: this.admissionInfo.academicYearId,
        providerId: this.admissionInfo.companyId,
        disciplineTypeId: this.admissionInfo.disciplineTypeId,
        validateUserProfile: false, //para no evaluar el perfil de usuario.
        validateLicense: true,
      }).then((res) => {
        this.collections.writerSpecialists = res.data;
      });
    },
    async getAbsenceReasons() {
      const { data } = await getAbsenceReason();
      this.collections.absenceReasons = data;
    },
    async getSpecialistInfo() {
      if (this.specialistInfo.serviceSpecialist == null) {
        this.specialistInfo.selectedServiceSpecialist = {
          fullName: '',
          licenceNo: '',
          expirationDate: '',
        };
        return;
      }

      await _getSpecialistInfo(
        this.specialistInfo.serviceSpecialist.id,
        this.admissionInfo.disciplineTypeId
      ).then((res) => {
        this.specialistInfo.selectedServiceSpecialist.fullName =
          res.data.fullName;
        this.specialistInfo.selectedServiceSpecialist.licenceNo =
          res.data.licenceNo;
        this.specialistInfo.selectedServiceSpecialist.expirationDate = this.formatDate(
          res.data.expirationDate
        );
      });
    },
    formatDate(date) {
      if (date) {
        return moment(date)
          .locale('es-PR')
          .format('DD/MMM/YYYY');
      }
    },
    showIfContainStatus(arrayStatusIds) {
      return arrayStatusIds.some(
        (statusId) => statusId == this.admissionInfo.admissionStatusId
      );
    },
    getSysParemeterStaticValue(parameterName) {
      return (this.sysParameters.find((x) => x.name === parameterName) || {})
        .staticValue;
    },
    async saveDocumentPlanHandler() {
      if (this.isSignHubActive) {
        const isValid = await this.$refs.interventionPlanSignatureDocument.validate();
        if (!isValid) {
          this.validationToast();
          this.tabModel = 1;
          return;
        }
      } else {
        if (!this.document.file) {
          this.validationToast();
          return;
        }
      }

      this.editingDocument = false;

      const params = objectToFormData(
        {
          id: Number(this.interventionPlanId),
          admissionId: this.admissionId,
        },
        { indices: true }
      );

      if (this.document && this.document.file && !this.document.documentId) {
        params.append(`document`, this.document.file, this.document.file.name);
      }

      await saveDiscussedDocument(params)
        .then(async () => {
          this.ShowToast(
            'Confirmación',
            `La información ha sido guardada exitosamente.`,
            'success'
          );
          await this.getInterventionPlan();
        })
        .catch((err) => {
          if (err.response != null) {
            this.ShowToast(
              'Alerta',
              err.response.data.message || 'error',
              'error',
              5000
            );
          }
        });
    },
    async onSaveAbsenceInterventionHandler(model) {
      try {
        const payload = {
          interventionPlanId: Number(this.interventionPlanId),
          date: model.date,
          absenceReasonId: model.absenceReason.id,
          otherReason: model.otherAbsenceReason,
        };

        await saveAbsence(payload);

        this.showAbsenceDocumentation = false;

        this.ShowToast(
          'Confirmación',
          `La información ha sido guardada exitosamente.`,
          'success'
        );
      } catch (err) {
        this.ShowToast(
          'Alerta',
          err.response.data.message || 'error',
          'error',
          5000
        );
      }
    },
    async handleSelectMechanism(selected) {
      if (this.interventionPlanId) {
        if (
          this.selectedMechanismSignPlan &&
          selected?.value === false &&
          !!this.signatureHubInfo?.signatureHubId
        ) {
          const answer = await this.ShowCancelModal(
            'Confirmación',
            'Usted ya se encuentra en un proceso de configuración de firma digital. ¿Está seguro que desea cambiar la configuración a manuscrito?',
            'Si',
            'No'
          );
          if (!answer) {
            this.$nextTick(() => {
              this.selectedMechanismSignPlan = true;
              this.$refs.signPlanRef?.setSelectedQuestion(true);
            });
            return;
          } else {
            const response = await _removeSignatureHub(
              this.signatureTypeId,
              this.relatedId
            ).catch((error) => {
              this.ShowToast('', error.response.data.message, 'error');
            });
            if (response?.data) {
              await this.getSignatureHubInfo();
            } else {
              this.$nextTick(() => {
                this.selectedMechanismSignPlan = true;
                this.$refs.signPlanRef?.setSelectedQuestion(true);
              });
              return;
            }
          }
        }
        if (
          this.canCompleteSelectedMechanism &&
          typeof selected?.value === 'boolean' &&
          selected?.value !== this.selectedMechanismSignPlan
        ) {
          await _saveInterventionPlanMechanism(
            this.signatureTypeId,
            this.relatedId,
            selected.value
          ).catch((error) => {
            this.ShowToast('', error.response.data.message, 'error');
          });
          if (selected?.value === true) {
            await this.getSignatureHubInfo();
          }
        }
      }
      this.selectedMechanismSignPlan = selected.value;
    },
    async handleConfigureSignatures() {
      this.$router.push({
        name: 'SignatureHub',
        params: {
          typeId: this.signatureTypeId,
          relatedId: this.relatedId,
          from: this.signatureHubFrom,
          fromData: {
            admissionId: this.admissionId,
            academicYearId: this.academicYearId,
            interventionPlanId: this.interventionPlanId,
            tabModel: this.tabModel,
          },
        },
      });
    },
    async getSignatureHubInfo() {
      const response = await _getSignatureHubInfo(
        this.signatureTypeId,
        this.relatedId
      );
      if (response?.data) {
        this.signatureHubInfo = response.data;
      }
    },
    async getAchievementSummaryInfo() {
      if (this.isSignHubActive) {
        this.selectedMechanismSignPlan = this.lastAchievement?.isElectronicSignature;
        this.signatureHubInfo = null;
        await this.getAchievementSummaryDocument();
        if (this.lastAchievement?.isElectronicSignature) {
          this.getSignatureHubInfo();
        }
      }
    },
    async saveAchievementSummaryDocumentHandler() {
      const isValid = await this.$refs.interventionPlanSignatureDocument.validate();
      if (!isValid) {
        this.validationToast();
        this.tabModel = 1;
        return;
      }

      const params = objectToFormData(
        {
          id: Number(this.interventionPlanId),
          admissionId: this.admissionId,
          documentTypeId: this.achievementDocumentTypeId,
        },
        { indices: true }
      );

      if (this.document && this.document.file && !this.document.documentId) {
        params.append(`document`, this.document.file, this.document.file.name);
      }

      await _saveAchievementSummaryDocument(params)
        .then(async () => {
          this.ShowToast(
            'Confirmación',
            `La información ha sido guardada exitosamente.`,
            'success'
          );
          await this.getInterventionPlan();
          await this.getAchievementSummaryInfo();
        })
        .catch((err) => {
          if (err.response != null) {
            this.ShowToast(
              'Alerta',
              err.response.data.message || 'error',
              'error',
              5000
            );
          }
        });
    },
    async getAchievementSummaryDocument() {
      if (this.lastAchievement?.period?.periodId) {
        const response = await _getAchievementSummaryDocument(
          this.interventionPlanId,
          this.achievementDocumentTypeId
        );
        if (response.status === 200 || response.data) {
          this.document = {
            name: response.data.filename,
            documentType: response.data.documentTypeId,
            documentId: response.data.documentId,
            readOnly: true,
            inServer: true,
            file: null,
          };
          this.documentOriginal = { ...this.document };
          this.documentSaved = true;
          this.hasSignDocument = !!response.data.documentId;
        } else {
          this.document = { documentId: null, name: null };
          this.documentSaved = false;
          this.hasSignDocument = false;
        }
      }
    },
    handleGoBack() {
      this.$router.push({
        name: this.navParams.from,
        params: {
          from: 'interventionPlan',
          fromData: this.fromData,
        },
      });
    },
    handleAmendedPlanHistory() {
      this.$router.push({
        name: AMENDED_PLAN_HISTORY_ROUTE_NAME,
        params: {
          interventionPlanId: this.interventionPlanId,
          admissionId: this.admissionId,
          from: 'interventionPlan',
          fromData: {
            admissionId: this.admissionId,
            academicYearId: this.academicYearId,
            interventionPlanId: this.interventionPlanId,
          },
        },
      });
    },
    handlePlanDiscussion() {
      this.activeInvoicePlan = true;
      this.activeInvoicePlanByAction = true;
      this.editMode(true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';

::v-deep .intervention-plan-collapse {
  .card-header {
    background-color: map-get($colors, 'white');
    border-bottom: 1px solid #e1e1e1;
    border-top: 1px solid #e1e1e1;
    .add-button {
      margin-top: 0.2rem;
      @media only screen and (max-width: 768px) {
        margin-top: 0.5rem;
        float: none !important;
      }
    }
  }
  .card-body {
    box-shadow: none;
    padding: 0.8rem;
  }
}
::v-deep.intervention-plan-continer {
  .mobile-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-direction: row;
    flex-wrap: wrap;
  }
}

::v-deep .dropdown-toggle {
  padding: 3px 12px;
  color: #2db782 !important;
}

::v-deep .custom-nav-container {
  border-bottom: 1px solid map-get($colors, 'gray-5');
  .nav-item {
    .nav-link {
      font-size: 17.602px;
      font-weight: 700;
      color: map-get($colors, 'secondary-6');
      &.active {
        border-bottom: 3px solid map-get($colors, 'green');
        color: map-get($colors, 'green');
      }
      &.active:after {
        content: unset;
      }
    }
  }
}
</style>
